import React, { useEffect, useMemo, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import ErrorPage from './components/ErrorPage';
import ErrorBoundary from './components/ErrorBoundary';
import SplashScreen from './components/SplashScreen';
import WriteupDrawer from './components/WriteupDrawer';
import PixelBlast from './components/PixelBlast';
import './components/WriteupPage.css';
import './App.css';
import writeupsData from './data/writeupsData.json';

const SPLASH_LAST_SHOWN_KEY = 'homeSplashLastShownAt';
const SPLASH_RESHOW_MS = 10 * 60 * 1000;

function shouldShowHomeSplash() {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    // localStorage (not sessionStorage) so a return visit within the reshow
    // window doesn't replay the splash even in a fresh tab/session.
    const lastShownRaw = window.localStorage.getItem(SPLASH_LAST_SHOWN_KEY);
    const lastShown = Number(lastShownRaw);

    if (!Number.isFinite(lastShown) || lastShown <= 0) {
      return true;
    }

    return Date.now() - lastShown >= SPLASH_RESHOW_MS;
  } catch {
    // If storage is unavailable, keep splash behavior predictable.
    return true;
  }
}

function recordHomeSplashShown() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(SPLASH_LAST_SHOWN_KEY, String(Date.now()));
  } catch {
    // No-op when storage is unavailable.
  }
}

// Lazy load heavy components
const Writeups = lazy(() => import('./components/Writeups'));
const Certifications = lazy(() => import('./components/Certifications'));
const Projects = lazy(() => import('./components/Projects'));
const About = lazy(() => import('./components/About'));
const CtfEvent = lazy(() => import('./components/CtfEvent'));
const WriteupDetail = lazy(() => import('./components/WriteupDetail'));
const PlatformCategory = lazy(() => import('./components/PlatformCategory'));

// Loading fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    color: 'var(--cp-cyan)',
    fontFamily: 'var(--font-family)'
  }}>
    Decrypting...
  </div>
);

// Main home page component
function HomePage({ activeSection, setActiveSection }) {
  const initialShowSplash = useMemo(() => shouldShowHomeSplash(), []);
  const [triggerDecryption, setTriggerDecryption] = useState(!initialShowSplash);
  const [showSplash, setShowSplash] = useState(initialShowSplash);

  const handleSplashComplete = () => {
    recordHomeSplashShown();
    setShowSplash(false);
    setTriggerDecryption(true);
  };

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}

      <PixelBlast />

      <div className="terminal-frame">
        <Header activeSection={activeSection} setActiveSection={setActiveSection} />
        <main>
          <Hero triggerDecryption={triggerDecryption} />
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Writeups />
              <Projects />
              <About />
              <Certifications />
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </>
  );
}

function WriteupPage({ activeSection, setActiveSection }) {
  const { slug } = useParams();
  const entry = writeupsData.items.find(item => item.slug === slug);
  useEffect(() => {
    setActiveSection('writeups');
  }, [setActiveSection]);

  const initialDrawerState = useMemo(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.innerWidth > 1024;
  }, []);

  const [drawerOpen, setDrawerOpen] = useState(initialDrawerState);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  return (
    <>


      <div className={`writeup-shell ${drawerOpen ? 'drawer-open' : 'drawer-collapsed'}`}>
        <WriteupDrawer isOpen={drawerOpen} onToggle={toggleDrawer} />
        <div
          className="drawer-overlay"
          aria-hidden="true"
          onClick={drawerOpen ? toggleDrawer : undefined}
        ></div>
        <button
          type="button"
          className="drawer-floating-toggle"
          aria-label={drawerOpen ? 'Hide sidebar' : 'Show sidebar'}
          aria-expanded={drawerOpen}
          data-state={drawerOpen ? 'open' : 'closed'}
          onClick={toggleDrawer}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className="writeup-content">
          <div className="writeup-content-inner">
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                {entry?.kind === 'event' ? <CtfEvent key={slug} event={entry} /> : <WriteupDetail />}
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </>
  );
}

function App() {
  const [activeSection, setActiveSection] = useState('home');

  return (
    <Router>
      <Helmet>
        <title>m3m0rydmp | Cyberpunk Portfolio</title>
        <meta name="description" content="Cybersecurity portfolio and writeups showcase featuring CTF solutions, pentesting reports, and security research." />
      </Helmet>
      <Routes>
        {/* Main home page */}
        <Route
          path="/"
          element={<HomePage activeSection={activeSection} setActiveSection={setActiveSection} />}
        />

        <Route
          path="/writeups/:slug"
          element={<WriteupPage activeSection={activeSection} setActiveSection={setActiveSection} />}
        />

        <Route
          path="/writeups/platform/:platform"
          element={
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <PlatformCategory />
              </Suspense>
            </ErrorBoundary>
          }
        />

        {/* Error pages */}
        <Route path="/404" element={<ErrorPage statusCode={404} />} />
        <Route path="/403" element={<ErrorPage statusCode={403} />} />
        <Route path="/500" element={<ErrorPage statusCode={500} />} />
        <Route path="/502" element={<ErrorPage statusCode={500} />} />
        <Route path="/503" element={<ErrorPage statusCode={500} />} />

        {/* Catch all other routes and show 404 */}
        <Route path="*" element={<ErrorPage statusCode={404} />} />
      </Routes>
    </Router>
  );
}

export default App;
